import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { longDate } from "@/lib/time";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(success ? "checking" : "cancelled");
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    if (!success || !sessionId) return;
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refreshUser(); return; }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch (e) {
        if (e.response?.status === 401) return setState("login");
        if (e.response?.status === 404) return setState("failed");
      }
      if (++tries < 15) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [success, sessionId, refreshUser]);

  const view = {
    checking: [Loader2, "Confirming your payment…", "This only takes a few seconds.", "text-slate-300 animate-spin"],
    paid: user?.trialing
      ? [CheckCircle2, "Your free trial has started!", `No charge today. Your plan renews automatically on ${longDate(user.trial_ends_at)} — cancel anytime from your account.`, "text-[#00E5A3]"]
      : [CheckCircle2, "You're all set!", "Your subscription is active. Enjoy your trip.", "text-[#00E5A3]"],
    cancelled: [XCircle, "Checkout cancelled", user?.needs_card ? "No charge was made. Add a card anytime to start your free trial." : "No charge was made. You can subscribe anytime.", "text-[#FF7A7A]"],
    failed: [XCircle, "Payment didn't go through", "Please try again from the pricing page.", "text-[#FF7A7A]"],
    timeout: [Loader2, "Still processing", "Check your account page in a minute.", "text-[#FFB800]"],
    login: [XCircle, "Please log in", "Your payment went to Stripe. Log in to see your subscription — it will be on your account page.", "text-[#FFB800]"],
  }[state];
  const [Icon, title, text, cls] = view;

  return (
    <main className="max-w-md mx-auto px-4 py-24 text-center" data-testid={`payment-${state}`}>
      <Icon className={`h-14 w-14 mx-auto ${cls}`} />
      <h1 className="text-3xl font-extrabold mt-6">{title}</h1>
      <p className="text-slate-400 mt-2">{text}</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/explore" data-testid="payment-go-explore" className="px-6 py-3 rounded-full bg-[#FF3B6B] font-semibold">Explore</Link>
        <Link to="/account" data-testid="payment-go-account" className="px-6 py-3 rounded-full border border-[#26334D]">My account</Link>
      </div>
    </main>
  );
}
